import React, { createContext, useContext, useEffect, useState } from 'react';
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';

const SubscriptionContext = createContext();

export const useSubscription = () => useContext(SubscriptionContext);

// Placeholder keys - REPLACE THESE with your actual RevenueCat keys
const API_KEYS = {
  ios: 'appl_placeholder_key',
  android: 'goog_placeholder_key',
};

export const SubscriptionProvider = ({ children }) => {
  const [isPro, setIsPro] = useState(false); // Default to false
  const [currentCustomerInfo, setCurrentCustomerInfo] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initCustomerInfo = async () => {
      try {
        if (Capacitor.isNativePlatform()) {
          // Initialize RevenueCat
          await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

          if (Capacitor.getPlatform() === 'ios') {
            await Purchases.configure({ apiKey: API_KEYS.ios });
          } else if (Capacitor.getPlatform() === 'android') {
            await Purchases.configure({ apiKey: API_KEYS.android });
          }

          // Get initial customer info
          const customerInfo = await Purchases.getCustomerInfo();
          handleCustomerInfoUpdate(customerInfo.customerInfo);

          // Fetch offerings (products)
          await loadOfferings();
        } else {
          // Web environment - mock or disable
          console.log('Running on web, skipping RevenueCat init');
          // For development purposes on web, you might want to force true/false here
          // setIsPro(true); 
        }
      } catch (error) {
        console.error('Error initializing RevenueCat:', error);
      } finally {
        setLoading(false);
      }
    };

    initCustomerInfo();

    // Listen for updates (restores, new purchases from other devices, etc.)
    let listenerPromise = Promise.resolve();
    if (Capacitor.isNativePlatform()) {
      listenerPromise = Purchases.addCustomerInfoUpdateListener((info) => {
        handleCustomerInfoUpdate(info.customerInfo);
      });
    }

    return () => {
      listenerPromise.then(listener => {
        // Purchases.removeCustomerInfoUpdateListener(listener); 
      });
    };
  }, []);

  const loadOfferings = async () => {
    if (!Capacitor.isNativePlatform()) return;
    try {
      const offerings = await Purchases.getOfferings();
      if (offerings.current && offerings.current.availablePackages.length > 0) {
        setPackages(offerings.current.availablePackages);
      }
    } catch (error) {
      console.error('Error loading offerings:', error);
    }
  };

  const handleCustomerInfoUpdate = (info) => {
    setCurrentCustomerInfo(info);
    const isProActive = typeof info.entitlements.active['pro'] !== 'undefined';
    setIsPro(isProActive);
  };

  const purchasePackage = async (pack) => {
    if (!Capacitor.isNativePlatform()) {
      console.warn('Purchases not supported on web');
      return false;
    }
    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pack });
      handleCustomerInfoUpdate(customerInfo);
      return true;
    } catch (error) {
      if (!error.userCancelled) {
        console.error('Error purchasing package:', error);
        throw error;
      }
      return false;
    }
  };

  const restorePurchases = async () => {
    if (!Capacitor.isNativePlatform()) {
      console.warn('Purchases not supported on web');
      return null; // or throw
    }
    try {
      const { customerInfo } = await Purchases.restorePurchases();
      handleCustomerInfoUpdate(customerInfo);
      return customerInfo;
    } catch (error) {
      console.error('Error restoring purchases:', error);
      throw error;
    }
  };

  const value = {
    isPro,
    currentCustomerInfo,
    packages,
    loading,
    purchasePackage,
    restorePurchases,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
